import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Spin, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useAssistPointApiFetcher } from '../../Data/AssistPointApiFetcher';
import { fetchApplicationServices, enrichServicesWithEnrollment, ensureUserProfileId } from './services/applicationLookupService';
import { submitOutcomeUpdate } from './services/outcomeFunctionService';
import { getServiceType, getOutcomeStatus } from './utils/serviceTypeMapper';
import { ApplicationDetailService, OutcomeResponse, OutcomeUpdate } from './types/outcomeTypes';
import { PracticeOption } from '../../Types/auth';
import config from '../../config';
import LookupCard from './components/LookupCard';
import ResultBanner from './components/ResultBanner';
import ServiceCard, { ServiceCardHandle } from './components/ServiceCard';

const { Text } = Typography;

const ApplicationStatusEditor: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { fetchGET } = useAssistPointApiFetcher();

    // Practice options
    const [practiceOptions, setPracticeOptions] = useState<PracticeOption[]>([]);
    const [practiceOptionsLoading, setPracticeOptionsLoading] = useState(true);
    const [practiceOptionsError, setPracticeOptionsError] = useState(false);

    // Lookup state
    const [loadingLookup, setLoadingLookup] = useState(false);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [applicationId, setApplicationId] = useState<number | null>(null);
    const [services, setServices] = useState<ApplicationDetailService[]>([]);
    const [submitResults, setSubmitResults] = useState<OutcomeUpdate[]>([]);
    const [result, setResult] = useState<OutcomeResponse | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const cardRefs = useRef<(ServiceCardHandle | null)[]>([]);
    const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const readinessRef = useRef<Map<number, boolean>>(new Map());
    const [readyCount, setReadyCount] = useState(0);

    const SIDEBAR_BADGE: Record<string, { color: string; label: string }> = {
        'copay':                { color: '#1677ff', label: 'CP' },
        'admin_reimb_support':  { color: '#fa8c16', label: 'BI' },
        'other_education':      { color: '#722ed1', label: 'OE' },
        'other_limited_supply': { color: '#52c41a', label: 'OLS' },
        'free_drug':            { color: '#13c2c2', label: 'FD' },
        'admin_referral_to':    { color: '#eb2f96', label: 'RT' },
    };

    const handleReadyChange = useCallback((serviceId: number, ready: boolean) => {
        readinessRef.current.set(serviceId, ready);
        setReadyCount([...readinessRef.current.values()].filter(Boolean).length);
    }, []);

    const loadPracticeOptions = useCallback(async (signal: { cancelled: boolean }) => {
        setPracticeOptionsLoading(true);
        setPracticeOptionsError(false);
        try {
            const token = await getAccessTokenSilently();
            // Clear the cached GUID so ensureUserProfileId always calls GetAssistPointUser
            // on mount. ensureUserProfileId deduplicates concurrent callers so only one
            // request fires even when React StrictMode double-invokes this effect — the
            // root cause of the AP API returning 204 on every page refresh.
            sessionStorage.removeItem('userProfileExternalObjectId');
            await ensureUserProfileId(token);
            const profileId = sessionStorage.getItem('userProfileExternalObjectId');
            const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
            if (profileId) headers['userProfileExternalObjectId'] = profileId;

            const res = await axios.get(
                `${config.apiUrl}/Authorization/GetAuthorizedUserProfilePracticeOptions`,
                { headers }
            );
            const raw = res.data;
            const opts: PracticeOption[] = Array.isArray(raw) ? raw : Array.isArray(raw?.$values) ? raw.$values : [];
            if (!signal.cancelled) setPracticeOptions(opts);
        } catch (err) {
            console.error('Failed to load practice options:', err);
            if (!signal.cancelled) setPracticeOptionsError(true);
        } finally {
            if (!signal.cancelled) setPracticeOptionsLoading(false);
        }
    }, [getAccessTokenSilently]);

    // Fetch available practice options on mount.
    // ensureUserProfileId must run first so the GUID header is present for the
    // practice options request (AP API requires it on all practice-scoped calls).
    useEffect(() => {
        const signal = { cancelled: false };
        loadPracticeOptions(signal);
        return () => { signal.cancelled = true; };
    }, [loadPracticeOptions]);

    async function handleLookup(id: number, practiceId: number) {
        setLoadingLookup(true);
        setLookupError(null);
        setResult(null);
        setSubmitted(false);
        setSubmitResults([]);
        setServices([]);
        readinessRef.current.clear();
        setReadyCount(0);
        try {
            const token = await getAccessTokenSilently();
            await ensureUserProfileId(token);
            const data = await fetchApplicationServices(id, fetchGET, practiceId);
            const enriched = await enrichServicesWithEnrollment(data ?? [], fetchGET, practiceId);
            setServices(enriched);
            setApplicationId(id);
        } catch (err: any) {
            setServices([]);
            setApplicationId(null);
            setLookupError(err?.message ?? 'Failed to load application services. Check that the AP API is running and you are logged in.');
        } finally {
            setLoadingLookup(false);
        }
    }

    async function handleSubmit() {
        const serviceDetails = cardRefs.current
            .map((ref, i) => {
                const values = ref?.getValues();
                if (!values) return null;
                const svc = services[i];
                const serviceType = getServiceType(svc.serviceCategoryDescription);
                return {
                    serviceType,
                    _desc: svc.serviceCategoryDescription,
                    outcome: getOutcomeStatus(values.outcome),
                    outcomeDate: values.outcomeDate,
                    effectiveDateStart: values.effectiveDateStart || undefined,
                    effectiveDateEnd: values.effectiveDateEnd || undefined,
                    memberId: values.memberId || undefined,
                    awardAmount: values.awardAmount ? parseFloat(values.awardAmount) : undefined,
                    rxBin: values.rxBin || undefined,
                    rxPcn: values.rxPcn || undefined,
                    rxGroup: values.rxGroup || undefined,
                };
            })
            .filter(Boolean) as any[];

        if (serviceDetails.length === 0) return;

        const unknownTypes = serviceDetails
            .filter(s => s.serviceType === undefined)
            .map(s => s._desc);
        if (unknownTypes.length > 0) {
            setResult({ success: false, status: 'error', issues: [{ field: 'serviceType', message: `Unsupported service type${unknownTypes.length > 1 ? 's' : ''}: ${unknownTypes.join(', ')}. Contact development to add this mapping.` }], updated: [], responseId: '' });
            return;
        }

        const cleanedDetails = serviceDetails.map(({ _desc, ...rest }) => rest);

        // All services on an application share the same AssistanceProgramId.
        const programId = String(services[0].assistanceProgramId);

        setSubmitting(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await submitOutcomeUpdate(
                { applicationId: applicationId!, serviceDetails: cleanedDetails },
                token,
                programId
            );
            setResult(response);
            setSubmitResults(response.updated ?? []);
            setSubmitted(true);
        } catch {
            setResult({ success: false, status: 'error', issues: [{ field: '', message: 'Request failed — check network or server logs.' }], updated: [], responseId: '' });
        } finally {
            setSubmitting(false);
        }
    }

    const hasServices = services.length > 0;

    return (
        <div style={{ display: 'flex', gap: 20, padding: '24px 24px 40px', alignItems: 'flex-start' }}>

            {/* ── Sticky left control panel ── */}
            <div style={{
                width: 260,
                flexShrink: 0,
                position: 'sticky',
                top: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-primary)',
                borderRadius: 8,
                overflow: 'hidden',
            }}>
                <div style={{ padding: '16px 18px' }}>
                    <LookupCard
                        onLookup={handleLookup}
                        loading={loadingLookup}
                        practiceOptions={practiceOptions}
                        practiceOptionsLoading={practiceOptionsLoading}
                        practiceOptionsError={practiceOptionsError}
                        onRetryPracticeOptions={() => {
                            const signal = { cancelled: false };
                            loadPracticeOptions(signal);
                        }}
                    />
                </div>

                {hasServices && (
                    <>
                        {/* App metadata */}
                        <div style={{ borderTop: '1px solid var(--border-primary)', padding: '10px 18px' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {services.length} service{services.length !== 1 ? 's' : ''} · Application #{applicationId}
                            </Text>
                        </div>

                        {/* Service nav list */}
                        <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 8, paddingBottom: 4 }}>
                            <div style={{
                                fontSize: 11, fontWeight: 600, letterSpacing: '0.07em',
                                textTransform: 'uppercase', color: 'var(--text-secondary)',
                                padding: '0 18px', marginBottom: 4,
                            }}>
                                Services
                            </div>
                            {services.map((svc, i) => {
                                const ready = readinessRef.current.get(svc.applicationServiceId) ?? false;
                                const badgeKey = Object.keys(SIDEBAR_BADGE).find(
                                    k => k === svc.serviceCategoryDescription?.toLowerCase()
                                );
                                const badge = badgeKey ? SIDEBAR_BADGE[badgeKey] : null;
                                const submitResult = submitResults.find(r => r.applicationServiceId === svc.applicationServiceId);
                                const isUnsupported = getServiceType(svc.serviceCategoryDescription) === undefined;
                                const dotColor = isUnsupported
                                    ? '#fa8c16'
                                    : submitted
                                        ? (submitResult ? '#52c41a' : '#d9d9d9')
                                        : (ready ? '#52c41a' : '#d9d9d9');
                                return (
                                    <button
                                        key={svc.applicationServiceId}
                                        onClick={() => containerRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 7,
                                            width: '100%', padding: '5px 18px',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <span style={{
                                            fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                            color: badge?.color ?? 'transparent',
                                            flexShrink: 0, minWidth: 24,
                                        }}>
                                            {badge?.label ?? ''}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: 12,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {svc.assistanceServiceName}
                                        </span>
                                        <span style={{
                                            width: 7, height: 7, borderRadius: '50%',
                                            flexShrink: 0, background: dotColor,
                                        }} />
                                    </button>
                                );
                            })}
                        </div>

                        {/* Submit section */}
                        <div style={{ borderTop: '1px solid var(--border-primary)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {!submitted && readyCount === 0 && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Fill in Outcome and Outcome Date on at least one service to enable submission
                                </Text>
                            )}
                            <Button
                                type="primary"
                                size="large"
                                icon={<ThunderboltOutlined />}
                                loading={submitting}
                                disabled={submitted || readyCount === 0}
                                onClick={handleSubmit}
                                style={{ width: '100%' }}
                            >
                                {readyCount > 0
                                    ? `Submit ${readyCount} Service${readyCount !== 1 ? 's' : ''}`
                                    : 'Submit Outcome Update'}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {/* ── Right card workspace ── */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {lookupError && (
                    <Alert type="error" showIcon message={lookupError} />
                )}

                {result && <ResultBanner result={result} />}

                {loadingLookup && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                )}

                {!loadingLookup && !hasServices && applicationId === null && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Enter an application ID to load its services"
                    />
                )}

                {!loadingLookup && !hasServices && applicationId !== null && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={`No services found for application #${applicationId}`}
                    />
                )}

                {hasServices && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {services.map((svc, i) => (
                            <div key={svc.applicationServiceId} ref={el => { containerRefs.current[i] = el; }}>
                                <ServiceCard
                                    ref={el => { cardRefs.current[i] = el; }}
                                    service={svc}
                                    submitResult={submitResults.find(r => r.applicationServiceId === svc.applicationServiceId)}
                                    disabled={submitted}
                                    onReadyChange={ready => handleReadyChange(svc.applicationServiceId, ready)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicationStatusEditor;
